import { AxiosError } from "axios";
import { useEffect } from "react";

import { switchOrg } from "api/api-server-actions";
import { MonitoringWebsocketProvider } from "providers/MonitoringWebsocketProvider";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentSection, selectOrgIdForSectionId } from "store/selectors";
import { selectCurrentUserId } from "store/selectors/base";
import { viewStateActions } from "store/slices/view";
import { reportApiFailure } from "utils/api-errors";
import { getOrgIdFromCookie } from "utils/auth";
import { rollbarAndLogWarning } from "utils/logger";
import { Outlet, useParams } from "utils/routing";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

export default function Layout() {
  const dispatch = useAppDispatch();
  const section = useAppSelector(selectCurrentSection);
  const sectionVersion = section?.section_version;
  // Ensure this component refreshes when we navigate between section routes
  const { sectionId } = useParams();
  const orgIdForSectionId = useAppSelector((s) => selectOrgIdForSectionId(s, sectionId));
  const orgIdFromCookie = getOrgIdFromCookie();
  const userId = useAppSelector(selectCurrentUserId);
  const needsOrgSwitch = orgIdForSectionId && orgIdForSectionId !== orgIdFromCookie;

  useEffect(() => {
    async function loadCourse() {
      if (!userId || !sectionId) {
        return;
      }
      // If we detect that we need to switch org, we skip the course retrieval
      if (needsOrgSwitch) {
        return;
      }
      try {
        await storeApi.sections.retrieve(sectionId, { skipToast: true });
        dispatch(viewStateActions.setCurrentSectionId(sectionId));
      } catch (error) {
        if (
          error instanceof AxiosError &&
          error.response?.status &&
          [404, 403, 401].includes(error.response.status)
        ) {
          rollbarAndLogWarning("Section not found, rerouting to index", error);
          window.location.href = "/";
        } else {
          reportApiFailure("Failed to load section", error);
        }
      }
    }
    loadCourse();
  }, [sectionId, dispatch, userId, needsOrgSwitch]);

  useEffect(() => {
    if (sectionVersion || sectionVersion === 0) {
      dispatch(viewStateActions.setSectionVersionTo(sectionVersion));
    }
  }, [sectionVersion, dispatch]);

  useEffect(() => {
    async function switchOrgIfNeeded() {
      if (!needsOrgSwitch || !userId) {
        return;
      }

      try {
        await switchOrg(orgIdForSectionId);
        window.location.reload();
      } catch (error) {
        // We don't show the error the user, because it's too technical. They will just see a 404.
        reportApiFailure("Failed to auto-switch org", error);
      }
    }

    switchOrgIfNeeded();
  }, [needsOrgSwitch, orgIdForSectionId, userId]);

  useEffect(() => {
    // Once we have the course and know it exists, we can start loading all related data
    if (section?.id && sectionId) {
      storeApi.sections.list();
      storeApi.section_users.list({ section_id: sectionId });
      storeApi.orgs.list();
      storeApi.class_sessions.list({ section_id: sectionId });
      storeApi.class_users.list({ section_id: sectionId });
      storeApi.recording_sessions.list({ section_id: sectionId });
    }
  }, [section?.id, sectionId]);

  return (
    <MonitoringWebsocketProvider>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </MonitoringWebsocketProvider>
  );
}
